import { Component } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyField, FormlyFieldProps } from '@ngx-formly/core';
import { humanizeFieldKey } from './formly.config';

export interface DataHubGroupProps extends FormlyFieldProps {
	/** Set by mapSchemaToField on single-property wrapper objects - see the `flatten` branch below. */
	flatten?: boolean;
}

/**
 * Renders `object`/`multischema` (oneOf/anyOf) schemas, and (registered under the 'formly-group'
 * type name too) the top-level fields array. Any nested group (has a `key`, i.e. isn't the bare
 * top-level usage) spans the full row width and gets its own `<fieldset><legend>` section, labeled
 * from the schema's own `title` or a humanized version of its property name - e.g. a `parserOptions`
 * property becomes a "Parser Options" section, with no hardcoding of specific field names. A nested
 * group with no properties (e.g. a `request` object some endpoints don't use) renders nothing at
 * all rather than an empty section with a legend and no fields.
 *
 * `props.flatten` groups skip the section entirely and let their single child render straight into
 * the parent's grid (already relabeled with the wrapper's name by mapSchemaToField) - a legend and
 * a border around one lone input is noise, not structure.
 */
@Component({
	selector: 'formly-nebular-group',
	template: `
		@if (field.key && field.fieldGroup?.length) {
			@if (props.flatten) {
				@for (f of field.fieldGroup; track f.id) {
					<formly-field [field]="f"></formly-field>
				}
			} @else {
				<div class="col-12 mb-2">
					<fieldset class="formly-nested-section">
						<legend>{{ props.label || humanizeFieldKey(field.key) }}</legend>
						<div class="row">
							@for (f of field.fieldGroup; track f.id) {
								<formly-field [field]="f"></formly-field>
							}
						</div>
					</fieldset>
				</div>
			}
		} @else if (!field.key) {
			<div class="row">
				@for (f of field.fieldGroup; track f.id) {
					<formly-field [field]="f"></formly-field>
				}
			</div>
		}
	`,
	styles: [
		`
			fieldset.formly-nested-section {
				padding: 0.75rem 1rem 1rem;
				border: 1px solid var(--divider-color);
				border-radius: 4px;

				legend {
					width: auto;
					padding: 0 0.5rem;
					margin-bottom: 0.5rem;
					font-size: 0.95rem;
					font-weight: 600;
				}
			}
		`
	],
	imports: [FormlyField]
})
export class FormlyNebularGroupTypeComponent extends FieldType<FieldTypeConfig<DataHubGroupProps>> {
	protected humanizeFieldKey = humanizeFieldKey;
}
